import api from './api';
import { openUploadedFile } from './fileService';
import {
    JobVacancy,
    VacancyStatus,
    JobApplication,
    CreateVacancyData,
    UpdateVacancyData,
    CreateApplicationData,
    UpdateApplicationStepData,
    VacancyStats,
    RecruitmentStep,
    AvailablePosition
} from '../types/jobVacancy';

const BASE_URL = '/api/job-vacancies';

export interface HireOptions {
    applications: { id: number; candidate_name: string; candidate_email: string; current_step: string; status: string }[];
    users: { id: number; name: string; email: string; role: string }[];
}

export interface HireRequest {
    application_id: number;
    user_id?: number;
    new_user?: { name: string; email: string; password: string; role: string };
    start_date: string;
    end_date?: string | null;
    allocation_percentage: number;
}
const UPLOAD_URL = '/api/uploads'; // New upload endpoint

export const jobVacancyService = {
    // Vagas
    async createVacancy(data: CreateVacancyData): Promise<JobVacancy> {
        const response = await api.post(`${BASE_URL}`, data);
        return response.data;
    },

    async getVacancies(params?: {
        status?: VacancyStatus[],
        project_id?: number,
        is_public?: boolean
    }): Promise<JobVacancy[]> {
        const response = await api.get(BASE_URL, { params });
        return response.data;
    },

    async getVacancy(id: number): Promise<JobVacancy> {
        const response = await api.get(`${BASE_URL}/${id}`);
        return response.data;
    },

    // Posições de projetos ativos ainda sem vaga (seleção em "Nova vaga")
    async getAvailablePositions(): Promise<AvailablePosition[]> {
        const response = await api.get(`${BASE_URL}/available-positions`);
        return response.data;
    },

    // Vagas públicas (sem login): o backend já omite salário e candidaturas
    async getPublicVacancies(): Promise<JobVacancy[]> {
        const response = await api.get(`${BASE_URL}/public`);
        return response.data;
    },

    async getPublicVacancy(id: number): Promise<JobVacancy> {
        const response = await api.get(`${BASE_URL}/public/${id}`);
        return response.data;
    },

    async applyToPublicVacancy(vacancyId: number, data: CreateApplicationData): Promise<void> {
        const formData = new FormData();
        formData.append('candidate_name', data.candidate_name);
        formData.append('candidate_email', data.candidate_email);
        formData.append('resume', data.resume);
        if (data.phone) formData.append('phone', data.phone);
        if (data.linkedin_url) formData.append('linkedin_url', data.linkedin_url);
        if (data.salary_expectation) formData.append('salary_expectation', String(data.salary_expectation));
        if (data.availability_date) formData.append('availability_date', data.availability_date);
        await api.post(`${BASE_URL}/public/${vacancyId}/applications`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // Currículos exigem token: baixa como blob e abre numa nova aba
    openResume(resumeUrl: string): Promise<void> {
        return openUploadedFile(resumeUrl);
    },

    // Fechar a vaga com quem foi contratado (já aloca a pessoa na posição)
    async getHireOptions(vacancyId: number): Promise<HireOptions> {
        return (await api.get(`${BASE_URL}/${vacancyId}/hire-options`)).data;
    },

    async hire(vacancyId: number, data: HireRequest): Promise<void> {
        await api.post(`${BASE_URL}/${vacancyId}/hire`, data);
    },

    async updateVacancy(id: number, data: UpdateVacancyData): Promise<JobVacancy> {
        const response = await api.put(`${BASE_URL}/${id}`, data);
        return response.data;
    },

    async updateVacancyStatus(id: number, status: VacancyStatus): Promise<JobVacancy> {
        const response = await api.put(`${BASE_URL}/${id}/status`, { status });
        return response.data;
    },

    async deleteVacancy(id: number): Promise<void> {
        await api.delete(`${BASE_URL}/${id}`);
    },

    // Candidaturas
    async createApplication(vacancyId: number, data: CreateApplicationData): Promise<JobApplication> {
        try {
            // Primeiro, vamos fazer upload do arquivo
            const formData = new FormData();
            formData.append('file', data.resume);
            formData.append('type', 'resumes');  // Especifica o tipo de arquivo
            
            // Upload do arquivo
            const uploadResponse = await api.post('/api/uploads', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Agora enviamos os dados da candidatura com a URL do arquivo
            const applicationData = {
                candidate_name: data.candidate_name,
                candidate_email: data.candidate_email,
                resume_url: uploadResponse.data.url,
                linkedin_url: data.linkedin_url,
                phone: data.phone,
                salary_expectation: data.salary_expectation,
                availability_date: data.availability_date
            };

            const response = await api.post(`${BASE_URL}/${vacancyId}/applications`, applicationData);
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 404 && error?.response?.config?.url?.includes('/uploads')) {
                throw new Error('Upload service is not available. Please try again later.');
            }
            throw error;
        }
    },

    async updateApplicationStep(
        vacancyId: number,
        applicationId: number,
        data: UpdateApplicationStepData
    ): Promise<JobApplication> {
        const response = await api.put(
            `${BASE_URL}/${vacancyId}/applications/${applicationId}/step`,
            data
        );
        return response.data;
    },

    // Estatísticas
    async getVacancyStats(): Promise<VacancyStats> {
        const response = await api.get(`${BASE_URL}/stats`);
        return response.data;
    },

    // Notificações
    async markTechnicalUpdatesSeen(vacancyId: number): Promise<void> {
        await api.post(`${BASE_URL}/${vacancyId}/mark-technical-updates-seen`);
    },

    async markNewCandidatesSeen(vacancyId: number): Promise<void> {
        await api.post(`${BASE_URL}/${vacancyId}/mark-new-candidates-seen`);
    },

    // Métodos adicionais
    async getProjectVacancies(projectId: number, positionId: number): Promise<JobVacancy[]> {
        const response = await api.get(`/api/projects/${projectId}/positions/${positionId}/vacancies`);
        return response.data;
    },

    async getVacancyApplications(vacancyId: number): Promise<JobApplication[]> {
        const response = await api.get(`${BASE_URL}/${vacancyId}/applications`);
        return response.data;
    },

    async getPendingApplicationsCount(vacancyId: number): Promise<number> {
        const response = await api.get(`${BASE_URL}/${vacancyId}/pending-applications`);
        return response.data.count;
    },

    async updateApplicationStatus(applicationId: number, data: { status: string; feedback?: string }): Promise<JobApplication> {
        const response = await api.put(`${BASE_URL}/applications/${applicationId}/status`, data);
        return response.data;
    }
}; 